import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionPanel } from '@angular/material/expansion';
import { MatTabGroup } from '@angular/material/tabs';
import { AuthService } from '@klickdata/core/auth';
import { ConfigService } from '@klickdata/core/config';
import { GlobalSearchData } from '@klickdata/core/global-search';
import { MediaType } from '@klickdata/core/media/src/media-type';
import { MediaService } from '@klickdata/core/media/src/media.service';
import { MessageSavedComponent, MessageService } from '@klickdata/core/message';
import { MessageErrorComponent } from '@klickdata/core/message/src/message-error/message-error.component';
import { MobileService, SideNaveActionsTypes, SideNaveDataTypes } from '@klickdata/core/mobile';
import { ResourceMediaTypes } from '@klickdata/core/question/src/question-type/question-type';
import { AppScope, Resource, ResourceCategory, ResourceTag } from '@klickdata/core/resource';
import { ResourceBuildingItems, ResourceTypes } from '@klickdata/core/resource/src/types.enum';
import { GenericTabsOption } from '@klickdata/shared-components/src/generic-mobile-tabs/generic-mobile-tabs.component';
import { MaterialsManagerCoreComponent } from '@klickdata/shared-components/src/materials-manager-core/materials-manager-core.component';
import { ResourceListingSheetComponent } from '@klickdata/shared-components/src/resource-listing-sheet/resource-listing-sheet.component';
import { DialogDisplayImgComponent } from 'apps/klickdata/src/app/shared/dialog/dialog-display-img/dialog-display-img.component';
import { OnDestroyHandler } from 'apps/klickdata/src/app/shared/onDestroy-handler/onDestroy-handler';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { catchError, filter, first, map, shareReplay, switchMap, takeUntil } from 'rxjs/operators';
import * as moment from 'moment';
import { FormHelper } from '@klickdata/core/form';

@Component({
    selector: 'app-course-manager-core-material-manager',
    templateUrl: './course-manager-core-material-manager.component.html',
    styleUrls: ['./course-manager-core-material-manager.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CourseManagerCoreMaterialManagerComponent
    extends OnDestroyHandler
    implements OnInit, AfterViewInit, OnChanges
{
    @Input() display: ResourceBuildingItems;
    @Input() resource: Resource;
    @Input() isEditInBottomSheet: boolean;
    @Input() opened: number;
    public resourceForm: FormGroup;
    ResourceBuildingItems = ResourceBuildingItems;
    @Output() saved: EventEmitter<{ res: Resource; isParentSubmission?: boolean }> = new EventEmitter<{
        res: Resource;
        isParentSubmission?: boolean;
    }>();
    public loadingUploader: Subject<boolean> = new Subject<boolean>();
    public isAIUploadedImg: Subject<boolean> = new Subject<boolean>();
    public mediaAIPrompterText: BehaviorSubject<string> = new BehaviorSubject<string>(null);

    @ViewChild('materialManager') materialManager: MaterialsManagerCoreComponent;
    @ViewChild('coverImagePanel') coverImagePanel: MatExpansionPanel;
    @ViewChild('mainTabs') mainTabs: MatTabGroup;
    AppScope = AppScope;
    public isMobile: Observable<boolean>;
    public isAdmin: boolean;
    public showCreationOption: boolean;
    public defaultResourceGoalCount: number;
    public materialIsSubmitted: boolean;
    ResourceTypes = ResourceTypes;
    constructor(
        protected auth: AuthService,
        protected mobile: MobileService,
        protected cdref: ChangeDetectorRef,
        protected fb: FormBuilder,
        protected mediaService: MediaService,
        protected messageService: MessageService,
        protected configService: ConfigService,
        protected dialog: MatDialog,
        protected bottomSheet: MatBottomSheet
    ) {
        super();
        this.resourceForm = this.fb.group({
            id: [],
            // priority: [],
            bullets: [],
            media_id: [],
            title: ['', Validators.required],
            category_ids: [[], Validators.required],
            tag_ids: [[]],
            description: [''],
            instructions: [''],
            language_id: ['', Validators.required],
            published: [],
        });
        this.defaultResourceGoalCount = this.configService.config.defaultResourceGoalCharCount;
    }
    ngOnInit() {
        this.isMobile = this.mobile.isMobile().pipe(shareReplay());
        this.auth
            .getUser()
            .pipe(
                first(),
                map((user) => user.isAdmin())
            )
            .subscribe((isAdmin) => {
                this.isAdmin = isAdmin;
                this.cdref.markForCheck();
            });
    }
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['resource']) {
            this.update();
        }
    }
    public update() {
        if (!this.resource?.id) {
            return;
        }
        this.resourceForm.patchValue({
            id: this.resource.id,
            priority: this.resource.priority,
            media_id: this.resource.media_id,
            bullets: this.resource.bullets,
            title: this.resource.title,
            instructions: this.resource.instructions,
            description: this.resource.description,
            category_ids: this.resource.category_ids,
            language_id: this.resource.language_id,
        });
        this.cdref.markForCheck();
        FormHelper.resetForm(this.resourceForm);
    }
    submitMaterial() {
        this.materialIsSubmitted = true;
        if (!this.resource?.id) {
            this.resourceForm.get('published').patchValue(moment().format('YYYY-MM-DD HH:mm:ss'));
        } else {
            this.resourceForm.removeControl('published');
        }
        this.materialManager.onSubmit();
    }
    onSaved(ev: Resource) {
        if (this.materialIsSubmitted) {
            this.saved.emit({ res: ev });
        }
    }
    public editInBottomSheet() {
        this.messageService.openMessage(
            MessageSavedComponent,
            $localize`This action can be done when edit in the source, please click on 'Edit ${AppScope.label(
                this.resource.scope_id,
                false
            )} source' button`
        );
    }
    ngAfterViewInit(): void {
        if (this.materialManager && this.display) {
            switch (this.display) {
                case ResourceBuildingItems.CREATE_NEW_FILE_MATERIAL:
                    this.materialManager.addNewItem({ item: { title: 'File' } });
                    break;
                case ResourceBuildingItems.CREATE_NEW_LINK_MATERIAL:
                    this.materialManager.addNewItem({ item: { item_type_value: 'url_material' } });
                    break;

                case ResourceBuildingItems.CREATE_NEW_TEXT_MATERIAL:
                    this.materialManager.addNewItem({ item: { item_type_value: 'text_material' } });
                    break;
                case ResourceBuildingItems.CREATE_AI_PROMPTER_MATERIAL:
                    this.materialManager.showPrompter.next(true);
                    break;
                default:
                    break;
            }
            this.cdref.markForCheck();
        }
    }
    public showTagResources(tag: ResourceTag) {
        this.showResources({ id: tag.id, label: tag.name, scope_id: AppScope.TAGS });
    }
    public showCatgeoryResources(category: ResourceCategory) {
        this.showResources({ id: category.id, label: category.title, scope_id: AppScope.CATEGORIES });
    }
    private showResources(item: GlobalSearchData) {
        this.bottomSheet.open(ResourceListingSheetComponent, {
            data: {
                id: item.id,
                label: item.label,
                scope_id: item.scope_id,
            },
            panelClass: 'sheet-wrapper',
        });
    }
    public showImgModal(id: number) {
        this.dialog.open(DialogDisplayImgComponent, {
            maxWidth: '70%',
            maxHeight: '50vh',
            data: id,
        });
    }
    public addMedia(errorMsg?: string) {
        if (this.resourceForm.value.media_id) {
            this.mainTabs.selectedIndex = 1;
            this.coverImagePanel.open();
            return;
        }
        this.mobile.updateSideNavSub({
            dataType: SideNaveDataTypes.ADD_RESOURCE_MEDIA,
            data: {
                icon: 'add_photo_alternate',
                title: $localize`Add an image`,
                description: $localize`Please select your preferred way to add an image.`,
                type: ResourceTypes.GeneralMaterial,
                errorMsg: errorMsg,
            },
        });
        this.getMediaTypeFromSlideIn();
    }
    private getMediaTypeFromSlideIn() {
        this.mobile
            .getSideNavAction()
            .pipe(
                filter((action) => action === SideNaveActionsTypes.POSITIVE),
                switchMap(() => this.mobile.getSideNavResponseData()),
                filter((data) => data.type === ResourceTypes.GeneralMaterial),
                takeUntil(this.destroy)
            )
            .subscribe((data) => {
                if (data && data.value) {
                    this.resourceForm.patchValue({ media_id: data.mediaId });
                    if (data.value === ResourceMediaTypes.FILE) {
                        this.isAIUploadedImg.next(false);
                        this.mediaAIPrompterText.next(null);
                    } else {
                        this.loadingUploader.next(true);
                        this.initAIUpload(data);
                    }
                    this.resourceForm.markAsDirty();
                }
            });
    }
    private initAIUpload(data: any) {
        this.mediaService
            .uploadMediaByLink({
                prompt: data.aiText,
                type: MediaType.PROMPT,
                scope_id: ResourceTypes.scopeByChildType(ResourceTypes.GeneralMaterial),
            })
            .pipe(
                takeUntil(this.destroy),
                catchError((err) => {
                    this.handleAIRequestFailure(err);
                    return of(null);
                })
            )
            .subscribe((media) => {
                this.loadingUploader.next(false);
                if (media && media.id) {
                    this.isAIUploadedImg.next(true);
                    this.resourceForm.patchValue({ media_id: media.id });
                    this.mediaAIPrompterText.next(data.aiText);
                }
            });
    }
    handleAIRequestFailure(err) {
        if (err && err.error && err.error.error) {
            this.messageService.openMessage(MessageErrorComponent, err.error.error.messages.join('/n'));
            this.addMedia(err.error.error.messages.join('/n'));
        }
    }

    submit() {
        // if (this.display !== ResourceBuildingItems.MATERIAL_LIBRARY) {
        //     this.materialManager.submit();
        // }
    }
}
